import { Button, Icon, Tooltip } from "@material-ui/core";
import { CheckCircleRounded } from "@material-ui/icons";
import firebase from "firebase/compat/app";
import React, { Component } from "react";
import styled from "styled-components";

import {
    LibraryView,
    PresentationFilters
} from "common/constants";
import { ActionPermissionsObject, IWorkspaceResourcePresentation, IWorkspaceResourceWithDocument, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import { getStaticUrl, isOpenaiApp } from "js/config";
import PresentationLibraryPaneController, {
    PresentationLibraryFilter,
    PresentationLibraryFilterType,
    PresentationLibraryPaneControllerState,
    PresentationLibraryPresentation,
} from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, { getWorkspaceResourcesWithDocuments, WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import ux from "js/core/services/userExperiences";
import isConnected from "js/core/utilities/isConnected";
import { User } from "js/core/models/user";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import ShortcutPanel from "js/editor/PresentationEditor/Components/ShortCutPanel";
import { PresentationLibraryGrid } from "js/editor/PresentationLibrary/PresentationLibraryGrid";
import { PresentationLibraryMobileIntro } from "js/editor/PresentationLibrary/PresentationLibraryMobileIntro";
import { PresentationLibraryList, PresentationLibraryListHeader } from "js/editor/PresentationLibrary/PresentationLibraryList";
import { PresentationLibraryPaneControllerInitializer, PresentationLibraryPaneControllerInitializerProps } from "js/editor/PresentationLibrary/PresentationLibraryPaneControllerInitializer";
import { PresentationLibraryShareNoticeMessage } from "js/editor/PresentationLibrary/PresentationLibraryShareNoticeMessage";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { LibraryPaneLayout } from "js/editor/PresentationLibrary/LibraryPaneLayout";
import { LibraryTemplatesPane } from "js/editor/PresentationLibrary/LibraryTemplatesPane";
import { PresentationLibrarySideBar } from "js/editor/PresentationLibrary/PresentationLibrarySideBar";
import { SearchAndAdd, SearchAndAddAction } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildFolderBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
    PresentationLibraryPaneBreadcrumbData,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { Icon as BIcon } from "js/Components/Icon";
import {
    PresentationLibraryFolderHeaderMenu,
} from "js/editor/PresentationLibrary/components/PresentationLibraryFolderMenu";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import {
    ShowDialog,
    ShowDialogAsync,
    ShowConfirmationDialog,
    ShowErrorDialog,
    ShowMessageDialog,
    ShowOfflineDialog,
    ShowWarningDialog,
} from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { CreatePresentationInSharedFolderDialog } from "js/react/components/Dialogs/CreatePresentationInSharedFolderDialog";
import { FlexSpacer, Gap10, Gap20, Gap30, Gap5 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, Notice, ResourceNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import {
    StyledWarningIcon,
    UIPane,
    UIPaneContents,
    UIPaneSideBar,
    UIPaneWithSideBar,
    WarningBox,
    WarningButton
} from "js/react/components/UiComponents";
import { browserHistory } from "js/react/history";
import { themeColors } from "js/react/sharedStyles";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { Layout, PaneBody, PaneContent } from "js/react/views/SharedResources/Components";
import { enterFullscreen } from "../../../openaiApp/utils/openai";
import { isCompactLibrary } from "./utils/isCompactLibrary";
import { HeaderActionPill, TemplatesListHeaderRow } from "js/editor/PresentationLibrary/libraryTemplatesPaneShared";

const logger = getLogger(LogGroup.LIBRARY);

const CloseButtonContainer = styled.div < { isMobile?: boolean } > `
    text-align: right;
    margin-bottom: -15px;
    margin-top: 10px;
    padding: ${props => props.isMobile ? "0 20px" : "0"};
`;

const RemoteControlBanner = styled.div<{ blue?: boolean }>`
    padding: 10px;
    text-align: center;
    text-wrap: nowrap;
    background: ${props => props.blue ? themeColors.ui_blue : "orangered"};
    color: white;
    font-size: 18px;
    width: 100%;
    font-weight: 600;
`;

const SubFolder = styled.div<{ isSelected: boolean }>`
    background: ${props => props.isSelected ? "white" : "#e1e1e1"};
    padding: 10px 15px 10px 10px;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    span {
        font-weight: 400;
    }

    .MuiIcon-root {
        opacity: 0.5;
    }

    &:hover {
        background: ${themeColors.selection};
    }
`;

const CreatePresentationButtonIntegrator = styled(Button)`
    height: 50px;
    border: 1px solid ${themeColors.ui_blue} !important;
    padding: 0 !important;
    margin-right: 14px !important;

    .MuiButton-label {
        color: #444;
        background: transparent;
        padding: 10px 20px;
    }

    .dark-mode & {
        .MuiButton-label {
            color: #ccc;
        }
    }
`;

const StyledRoundedIcon = styled(CheckCircleRounded)`
    &.MuiSvgIcon-colorPrimary {
        width: 16px;
        height: 16px;
        margin-right: 8px;
    }
`;

const HiddenScrollBox = styled(ScrollBox)`
    /* Hide scrollbar for WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for Firefox */
    scrollbar-width: none;

    /* Hide scrollbar for IE and Edge Legacy */
    -ms-overflow-style: none;
`;

const StyledRenderItemsContainer = styled.div`
    padding: 0 20px;
    box-sizing: border-box;
`;

const PaneColumn = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const PresentationListHeaderBreadcrumbGroup = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;
`;

const PresentationListHeaderFolderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 10px;
`;

const LibraryScrollBox = styled(ScrollBox)<{ $reserveScrollbarGutter?: boolean }>`
    ${({ $reserveScrollbarGutter }) => $reserveScrollbarGutter && "scrollbar-gutter: stable;"}
`;

const LibraryContentArea = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    flex-grow: 2;
`;

type PresentationLibraryPaneProps = WorkspaceControllerState & PresentationLibraryPaneControllerState & {
    workspaceId: string;
    firebaseUser: firebase.User;
    readOnly?: boolean;
    pane?: PresentationLibraryFilterType;
    folderId?: string;
    subFolderId?: string;
    user: typeof User;
    onSelectedPresentation: (presentationId: string) => void;
    onCloseDialog: () => void;
    showCloseIcon: boolean;
    showSidebar: boolean;
    onHideSidebar: () => void;
    sideBarWidth: number;
    onResizeSidebar: () => void;
    showNavigationSidebar?: boolean;
    className: string;
    showLibraryViewSelect: boolean;
    isMyPresentationsOnly: boolean;
    onReady?: () => void;
    onDoubleClick?: () => void;
    view?: typeof LibraryView.LIBRARY | typeof LibraryView.TEMPLATES;
}

interface InnerPresentationLibraryPaneState {
    draggedItems: PresentationLibraryPresentation[];
    infoBannerClosed: boolean;
    listHeaderWidth: number;
}

// Base class with shared logic
export class PresentationLibraryPaneBase extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
    sideBarRef: React.RefObject<any>;
    scrollRef: React.RefObject<any>;
    scrollResizeObserver?: ResizeObserver;
    observedScrollElement?: HTMLElement;
    folderDropHandled = false;

    get sortOptions() {
        return [{
            field: "document.name", label: t("Name"), direction: "asc", icon: "sort_by_alpha"
        }, {
            field: "document.createdAt", label: t("Recently Created"), direction: "desc", icon: "calendar_month"
        }, {
            field: "document.modifiedAt", label: t("Recently Edited"), direction: "desc", icon: "edit_calendar"
        }];
    }

    constructor(props) {
        super(props);

        this.sideBarRef = React.createRef();
        this.scrollRef = React.createRef();

        this.state = {
            draggedItems: [],
            infoBannerClosed: false,
            listHeaderWidth: 0
        };
    }

    handleInfoBannerClose = () => {
        localStorage.setItem("bai-mobile-info-banner-closed", "true");
        this.setState({ infoBannerClosed: true });
    }

    componentDidMount() {
        if (localStorage.getItem("bai-mobile-info-banner-closed") === "true") {
            this.setState({ infoBannerClosed: true });
        }
        this.updateListHeaderWidth();
    }

    componentDidUpdate() {
        this.updateListHeaderWidth();
    }

    componentWillUnmount() {
        this.scrollResizeObserver?.disconnect();
    }

    updateListHeaderWidth = () => {
        const scrollElement = this.scrollRef.current?.ref?.current;
        if (!scrollElement) {
            return;
        }

        if (typeof ResizeObserver !== "undefined") {
            if (!this.scrollResizeObserver) {
                this.scrollResizeObserver = new ResizeObserver(() => this.updateListHeaderWidth());
            }
            if (this.observedScrollElement !== scrollElement) {
                if (this.observedScrollElement) {
                    this.scrollResizeObserver.unobserve(this.observedScrollElement);
                }
                this.scrollResizeObserver.observe(scrollElement);
                this.observedScrollElement = scrollElement;
            }
        }

        const listHeaderWidth = scrollElement.clientWidth;
        if (listHeaderWidth && listHeaderWidth !== this.state.listHeaderWidth) {
            this.setState({ listHeaderWidth });
        }
    }

    handleSetFilter = (filter: PresentationLibraryFilter) => {
        const { readOnly } = this.props;

        // if we are in read-only (We're adding a slide from My Slide sectio ) mode
        // we should just update the filter state because we
        // don't want to navigate away from the current page
        if (readOnly) {
            PresentationLibraryPaneController.setLibraryFilter(filter);
        } else {
            AppController.showLibrary({ filter });
        }
    }

    handleSelectedPresentations = async (selection: PresentationLibraryPresentation[]) => {
        const { onSelectedPresentation } = this.props;

        if (selection.length === 0) {
            return;
        }

        if (onSelectedPresentation) {
            onSelectedPresentation(selection[0].id);
            return;
        }

        if (isCompactLibrary()) {
            await this.handleDoubleClickedPresentation(selection[0]);
        }
    }

    handleDoubleClickedPresentation = async (presentation: PresentationLibraryPresentation) => {
        if (this.props.onDoubleClick) {
            return this.props.onDoubleClick();
        }

        if (presentation.trashedAt) {
            return ShowErrorDialog({
                title: t("Can't edit presentations in the trash"),
                message: t("Please restore this presentation back into your library before editing.")
            });
        }

        // zoom app cases (meeting and non-meeting views)
        if (window.isZoomApp) {
            if (window.roomID && window.roomPresentationStarted) {
                return await window.roomPresentationStarted(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: t("Error Starting Presentation"),
                        message: err.message
                    }));
            }

            // @ts-ignore
            if (!window.roomID && window.roomPresentationPreviewed) {
                // @ts-ignore
                return await window.roomPresentationPreviewed(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: t("Error Previewing Presentation"),
                        message: err.message
                    }));
            }
        }

        // show player if in a Meeting Room
        if (window.roomID) {
            PresentationLibraryPaneController.playPresentation(presentation.id);
            return;
        }

        // The embedded app uses its constrained library inline, then expands before opening the editor.
        // Preserve the player-only behavior for the regular mobile library and read-only presentations.
        if (!presentation.permissions.write || (isCompactLibrary() && !isOpenaiApp)) {
            PresentationLibraryPaneController.playPresentation(presentation.id, isOpenaiApp || !isCompactLibrary());
            return;
        }

        // show presentation editor
        if (isOpenaiApp) {
            const displayMode = await enterFullscreen();
            if (displayMode?.mode !== "fullscreen") {
                PresentationLibraryPaneController.playPresentation(presentation.id, true);
                return;
            }
        }
        AppController.showEditor({ presentationId: presentation.id });
    }

    handleStartDragItems = (selection: PresentationLibraryPresentation[]) => {
        this.folderDropHandled = false;
        ux.dragPresentationsToFolder.register({ presentationIds: selection.map(presentation => presentation.id) });
        this.setState({ draggedItems: selection });
    }

    handleFolderDropStart = () => {
        this.folderDropHandled = true;
    }

    handleDropDragItems = () => {
        if (!this.folderDropHandled) {
            ux.dragPresentationsToFolder.abandon();
        }
        this.folderDropHandled = false;
        this.setState({ draggedItems: [] });
    }

    handleSearch = (query: string) => {
        PresentationLibraryPaneController.setSearch({ query });
    }

    handleSort = (sort: { field: string; direction: "asc" | "desc" }) => {
        PresentationLibraryPaneController.setSort([sort]);
    }

    handleSetViewType = (viewType: "grid" | "list") => {
        PresentationLibraryPaneController.setViewType(viewType);
    }

    handleSubFolderMouseUp = async (subFolderId: string) => {
        const { draggedItems } = this.state;

        const filter = PresentationLibraryPaneController.libraryFilter;

        if (draggedItems?.length) {
            const resources = draggedItems.map(presentation => ({
                resourceId: presentation.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
            }));

            try {
                await withProgressDialog({
                    title: t("Adding {{count}} presentations to folder...", { count: resources.length }),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: subFolderId,
                            update: {
                                add: resources,
                            }
                        });

                        this.handleSetFilter({
                            type: PresentationLibraryFilterType.FOLDER,
                            folderId: filter.folderId,
                            subFolderId
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Error adding presentations to folder");

                ShowErrorDialog({
                    title: t("Error adding presentations to folder"),
                    message: err.message
                });
            }
        } else {
            this.handleSetFilter({
                type: PresentationLibraryFilterType.FOLDER,
                folderId: filter.folderId,
                subFolderId
            });
        }
    }

    handleEmptyTrash = async () => {
        const { workspaceId } = this.props;

        const records: IWorkspaceResourceWithDocument<{ id: string, softDeletedAt: number }, IWorkspaceResourcePresentation>[] = [];

        let total = Number.MAX_SAFE_INTEGER;

        while (total > records.length) {
            const response = await getWorkspaceResourcesWithDocuments({
                offset: records.length,
                size: 100,
                workspaceId,
                resourceTypes: [WorkspaceResourceType.PRESENTATION],
                filters: [{ field: "document.softDeletedAt", value: null, operator: "ne" }],
                documentFields: ["id", "softDeletedAt"]
            });
            records.push(...response.items);
            total = response.total;
        }

        if (records.length === 0) {
            ShowMessageDialog({
                title: t("No presentations in trash"),
                message: t("There are no presentations in the trash.")
            });
            return;
        }

        ShowConfirmationDialog({
            title: t("Are you sure you want to permanently delete all of the presentations in the trash?"),
            message: t("You can't undo this action."),
            acceptCallback: () => {
                withProgressDialog({
                    title: t("Deleting presentations in trash..."),
                    action: () =>
                        PresentationLibraryPaneController.deletePresentations(records.map(i => i.document.id))
                            .catch(err => {
                                logger.error(err, "handleEmptyTrash() failed to delete presentations in trash");

                                ShowErrorDialog({
                                    title: t("Error deleting presentations in trash"),
                                    message: err.message
                                });
                            })
                });
            }
        });
    }

    handleCreateNewPresentation = async (
        pane = CreatePresentationDialogPaneType.BLANK_PRESENTATION,
        { hideSidebar = false, createInFolder = false }: { hideSidebar?: boolean; createInFolder?: boolean } = {},
    ) => {
        const currentFilter = PresentationLibraryPaneController.libraryFilter;
        const libraryFilter = createInFolder
            ? currentFilter
            : { type: PresentationLibraryFilterType.ALL_PRESENTATIONS };
        const permissions = PresentationLibraryPaneController.currentFolderPermissions;
        const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
            ActionPermissionsObject.RESOURCES,
            PermissionActionType.CREATE
        );

        const launchCreatePresentationDialog = () => {
            if (app.integrator) {
                window.open("/", "_blank");
                return;
            }

            AppController.showCreatePresentationDialog({
                pane,
                hideSidebar,
                libraryFilter,
            });
        };

        if (canUpgradeToCreateResources) {
            ShowDialog(ChangePlanDialog, {
                featuredPlan: pane === CreatePresentationDialogPaneType.TEAM_TEMPLATES
                    ? WorkspacePlanTier.TEAM
                    : WorkspacePlanTier.PRO,
                analytics: { cta: "create_new_presentation" },
            });
            return;
        }

        if (!isConnected.connected) {
            ShowOfflineDialog();
            return;
        }

        if (createInFolder && currentFilter.type === PresentationLibraryFilterType.FOLDER) {
            if (!permissions.write.object) {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to create presentations in this team folder. Contact your Team Admin to request access."),
                });
                return;
            }

            const confirmed = await ShowDialogAsync(CreatePresentationInSharedFolderDialog, {
                folderId: currentFilter.subFolderId ?? currentFilter.folderId,
            });
            if (!confirmed) {
                return;
            }
        }

        launchCreatePresentationDialog();
    }

    getBreadcrumb(): PresentationLibraryPaneBreadcrumbData | null {
        const { resourceFolders } = this.props;

        const filter = PresentationLibraryPaneController.libraryFilter;

        switch (filter.type) {
            case PresentationFilters.ALL_PRESENTATIONS:
                return { breadcrumb: t("All Presentations") };
            case PresentationFilters.RECENT:
                return { breadcrumb: t("Recent Presentations") };
            case PresentationFilters.OWNED_BY_ME:
                return { breadcrumb: t("Created by me") };
            case PresentationFilters.SHARED_WITH_ME:
                return { breadcrumb: t("Shared With Me") };
            case PresentationFilters.FOLDER: {
                const folderId = filter.subFolderId || filter.folderId;
                const folder = resourceFolders.find(f => f.id === folderId);
                if (folder) {
                    return buildFolderBreadcrumbData(
                        folderId,
                        resourceFolders,
                        targetFolderId => {
                            this.handleSetFilter({
                                type: PresentationLibraryFilterType.FOLDER,
                                folderId: targetFolderId,
                            });
                        },
                    );
                }
                return null;
            }
            case PresentationFilters.TRASH:
                return { breadcrumb: t("Trash") };
        }

        return null;
    }

    renderItems(showListHeader = true) {
        const { readOnly, viewType, search, fetching } = this.props;

        if (!PresentationLibraryPaneController.initialized) {
            return <Spinner />;
        }

        const filter = PresentationLibraryPaneController.libraryFilter;
        const searchQuery = search.query;

        const aiBullets = [
            t("Create presentations in one click with AI"),
            t("Hundreds of Smart Slide templates"),
            t("Create your branded theme"),
            t("Be a presentation rockstar, make beautiful slides in minutes")
        ];

        const presentations = PresentationLibraryPaneController.legacyData;
        if (presentations.length === 0 && !fetching) {
            switch (filter.type) {
                case PresentationFilters.ALL_PRESENTATIONS:
                case PresentationFilters.OWNED_BY_ME:
                case PresentationFilters.RECENT:
                    //currently readOnly is set to true when viewing presentation library from CopySlidePane.
                    if (readOnly || searchQuery) {
                        return <NoMatchNotice />;
                    } else {
                        if (isCompactLibrary()) {
                            return (
                                <Notice
                                    title={t("You have no presentations in this workspace.")}
                                    message={t("Open Beautiful.ai in your desktop browser to create one.")}
                                />
                            );
                        } else {
                            if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE)) {
                                return (
                                    <ResourceNotice
                                        image={getStaticUrl("/images/notice-banner/library.svg")}
                                        title={t("There are no presentations in this workspace.")}
                                        message={t("You are a guest in this workspace. You can find presentations shared with you here.")}
                                    />
                                );
                            } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.CREATE)) {
                                return (
                                    <ResourceNotice
                                        image={getStaticUrl("/images/notice-banner/library.svg")}
                                        title={t("You don't have any presentations yet.")}
                                        message={
                                            <>
                                                <FlexBox>{t("Create presentations in minutes with automated design.")}</FlexBox>
                                                <div style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 1fr",
                                                    gap: "15px",
                                                    marginTop: "10px",
                                                    backgroundColor: "#F7FAFC",
                                                    padding: "15px",
                                                    borderRadius: "2px",
                                                    justifyItems: "start"
                                                }}>
                                                    {aiBullets.map(text => (
                                                        <FlexBox key={text}>
                                                            <StyledRoundedIcon color="primary" />
                                                            <span>{text}</span>
                                                        </FlexBox>
                                                    ))}
                                                </div>
                                            </>
                                        }
                                    />
                                );
                            }
                            return (
                                <Notice
                                    title={t("Click Create Presentation to get started.")}
                                />
                            );
                        }
                    }
                case PresentationFilters.SHARED_WITH_ME:
                    return (
                        <Notice
                            title={t("No one has shared a presentation with you.")}
                            message={(
                                <PresentationLibraryShareNoticeMessage />
                            )}
                        />
                    );
                case PresentationFilters.TRASH:
                    return (
                        <Notice
                            title={t("Trash is empty")}
                        />
                    );
                case PresentationFilters.FOLDER:
                    return (
                        <Notice
                            title={t("This folder is empty.")}
                            message={!isCompactLibrary() && t("You can drag presentations into this folder to add them.")}
                        />
                    );
                default:
                    return (
                        <Notice
                            title={t("No presentations found")}
                        />
                    );
            }
        }

        if (viewType === "grid") {
            return (
                <PresentationLibraryGrid
                    readOnly={readOnly}
                    onNavigateToFolder={(folderId, subFolderId) =>
                        this.handleSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId, subFolderId })
                    }
                    onSelectedPresentations={this.handleSelectedPresentations}
                    onDoubleClick={this.handleDoubleClickedPresentation}
                    onStartDragItems={this.handleStartDragItems}
                    onDropDragItems={this.handleDropDragItems}
                />
            );
        }

        return (
            <PresentationLibraryList
                showHeader={showListHeader}
                readOnly={readOnly}
                onNavigateToFolder={(folderId, subFolderId) =>
                    this.handleSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId, subFolderId })
                }
                onSelectedPresentations={this.handleSelectedPresentations}
                onDoubleClick={this.handleDoubleClickedPresentation}
                onStartDragItems={this.handleStartDragItems}
                onDropDragItems={this.handleDropDragItems}
            />
        );
    }
}

// Desktop version
class InnerPresentationLibraryPaneDesktop extends PresentationLibraryPaneBase {
    render() {
        const {
            readOnly,
            onCloseDialog,
            showCloseIcon,
            onHideSidebar,
            sideBarWidth,
            onResizeSidebar,
            showNavigationSidebar = true,
            className = "",
            initialized,
            fetching,
            search,
            sort,
            viewType,
            onReady = () => { }
        } = this.props;
        const { draggedItems, listHeaderWidth } = this.state;

        if (!initialized) {
            return (<UIPane>
                <Spinner />
            </UIPane>);
        }

        const filter = PresentationLibraryPaneController.libraryFilter;

        const breadcrumb = this.getBreadcrumb();
        const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;

        const isInFolder = !!(filter.folderId || filter.subFolderId);
        const showFolderCreateButton = isInFolder && !readOnly && !app.integrator && canCreateResources;
        const designerBotAvailable = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use;
        const canUpgradeToUseGenerativeAi = WorkspaceController.canUpgradeToRemoveActionRestriction(
            ActionPermissionsObject.GENERATIVE_AI,
            PermissionActionType.USE
        );
        const canUpgradeToUseDesignerBot = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC ||
            canUpgradeToUseGenerativeAi;
        const showCreateWithAiButton = designerBotAvailable || canUpgradeToUseDesignerBot || WorkspaceController.isPastDue;
        const searchActions: SearchAndAddAction[] = [];

        if (!app.integrator && canCreateResources && !readOnly) {
            if (showCreateWithAiButton) {
                const createWithAiAction: SearchAndAddAction = {
                    id: "create-with-ai-button",
                    label: t("Create with AI"),
                    onClick: () => {
                        // Preserve generate_with_ai cta for ManagePlanDialogOpen (same as UnlockAiGenerationBanner).
                        if (canUpgradeToUseDesignerBot) {
                            ShowDialog(ChangePlanDialog, {
                                featuredPlan: WorkspacePlanTier.PRO,
                                analytics: { cta: "generate_with_ai" },
                            });
                            return;
                        }

                        this.handleCreateNewPresentation(
                            CreatePresentationDialogPaneType.GENERATE_WITH_AI,
                            { hideSidebar: true },
                        );
                    },
                    variant: canUpgradeToUseGenerativeAi ? "upgrade" : "ai",
                };
                searchActions.push(createWithAiAction);
            }

            searchActions.push({
                id: "new-presentation-button",
                label: t("New Presentation"),
                onClick: () => this.handleCreateNewPresentation(),
                variant: "primary",
            });
        }

        const canManageSharedSlides = !app.integrator && !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type === PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    {t("Items in your trash will be permanently deleted after 30 days")}
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    {t("Empty Trash")}
                </WarningButton>
            </FlexBox>
        ) : null;

        const hasScrollableItems = PresentationLibraryPaneController.legacyData.length > 0 || fetching;
        const showDetachedListHeader = viewType === "list" && hasScrollableItems;
        const extendScrollableContent = (viewType === "list" || viewType === "grid") && hasScrollableItems;
        return (
            <LibraryPaneLayout
                className={className}
                libraryView={LibraryView.LIBRARY}
                onResizeSidebar={onResizeSidebar}
                showNavigationSidebar={showNavigationSidebar}
                sideBarWidth={sideBarWidth}
            >
                <FlexBox fill vertical top>
                    <PaneColumn>
                        <OnMountedReporter onMounted={onReady} />
                        <Layout>
                            {
                                showCloseIcon &&
                                <CloseButtonContainer>
                                    <Icon onClick={onCloseDialog}>
                                        close
                                    </Icon>
                                </CloseButtonContainer>
                            }

                            {
                                app.integrator &&
                                <>
                                    <Gap20 />
                                    <CreatePresentationButtonIntegrator
                                        id="new-presentation-button"
                                        variant="text"
                                        onClick={() => this.handleCreateNewPresentation()}
                                    >
                                        <Icon>add</Icon>{t("Create New Presentation")}
                                    </CreatePresentationButtonIntegrator>
                                </>
                            }

                            {window.roomID && (
                                window.isZoomApp
                                    ? <RemoteControlBanner blue>{t("Select presentation to share in Zoom meeting.")}</RemoteControlBanner>
                                    : <RemoteControlBanner>{t("Select a presentation to Remote Play")}</RemoteControlBanner>
                            )}
                            {!window.roomID && window.isZoomApp && (
                                <RemoteControlBanner blue>{t("Select a presentation to preview in player, please use the browser for full access.")}</RemoteControlBanner>
                            )}

                            {filter.type != PresentationFilters.TRASH && (
                                <SearchAndAdd
                                    onSearch={this.handleSearch}
                                    actions={searchActions}
                                />
                            )}
                            <PaneBody>
                                {!window.isZoomApp &&
                                    <PresentationLibrarySideBar
                                        filter={filter}
                                        onSetFilter={this.handleSetFilter}
                                        readOnly={readOnly}
                                        draggedItems={draggedItems}
                                        canEditLibraryItems={canManageSharedSlides}
                                        onHideSidebar={onHideSidebar}
                                        onFolderDropStart={this.handleFolderDropStart}
                                    />
                                }
                                <PaneContent>
                                    <TemplatesListHeaderRow>
                                        <PresentationListHeaderBreadcrumbGroup>
                                            {breadcrumb && (
                                                <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                                            )}
                                            {isInFolder && (showFolderCreateButton || !readOnly) && (
                                                <PresentationListHeaderFolderActions>
                                                    <HeaderActionPill>
                                                        <PresentationLibraryFolderHeaderMenu
                                                            filter={filter}
                                                            readOnly={readOnly}
                                                            onSetFilter={this.handleSetFilter}
                                                        />
                                                    </HeaderActionPill>
                                                    {showFolderCreateButton && (
                                                        <Tooltip title={t("Create new item in folder")}>
                                                            <HeaderActionPill data-test-id="create-presentation-in-folder-button">
                                                                <BIcon
                                                                    size={16}
                                                                    onClick={event => {
                                                                        event.stopPropagation();
                                                                        this.handleCreateNewPresentation(undefined, { createInFolder: true });
                                                                    }}
                                                                >
                                                                    add
                                                                </BIcon>
                                                            </HeaderActionPill>
                                                        </Tooltip>
                                                    )}
                                                </PresentationListHeaderFolderActions>
                                            )}
                                        </PresentationListHeaderBreadcrumbGroup>
                                        {filter.type != PresentationFilters.TRASH && (
                                            <PresentationListHeader
                                                sortOptions={this.sortOptions}
                                                sort={sort[0]}
                                                searchQuery={search.query}
                                                onSetSort={this.handleSort}
                                                viewType={viewType}
                                                onSetViewType={this.handleSetViewType}
                                            />
                                        )}
                                        {TrashHeader}
                                    </TemplatesListHeaderRow>
                                    {extendScrollableContent ? (
                                        <LibraryContentArea>
                                            {showDetachedListHeader &&
                                                <PresentationLibraryListHeader sort={sort} search={search} detached width={listHeaderWidth} />
                                            }
                                            <LibraryScrollBox id="presentation_list" ref={this.scrollRef}
                                                fillWidth
                                                $reserveScrollbarGutter
                                                style={{ height: 0, flexGrow: 2, position: "relative" }}
                                            >
                                                {this.renderItems(!showDetachedListHeader)}
                                            </LibraryScrollBox>
                                        </LibraryContentArea>
                                    ) : (
                                        <LibraryScrollBox id="presentation_list" ref={this.scrollRef}
                                            fillWidth
                                            style={{ height: 0, flexGrow: 2, position: "relative" }}
                                        >
                                            {this.renderItems()}
                                        </LibraryScrollBox>
                                    )}
                                </PaneContent>
                            </PaneBody>
                        </Layout>
                        <ShortcutPanel />
                    </PaneColumn>
                </FlexBox>
            </LibraryPaneLayout>
        );
    }
}

// Mobile version
class InnerPresentationLibraryPaneMobile extends PresentationLibraryPaneBase {
    render() {
        const {
            readOnly,
            onCloseDialog,
            showCloseIcon,
            showSidebar,
            onHideSidebar,
            sideBarWidth,
            onResizeSidebar,
            className,
            search,
            sort,
            viewType,
            onReady = () => { }
        } = this.props;
        const { draggedItems, infoBannerClosed } = this.state;

        const filter = PresentationLibraryPaneController.libraryFilter;
        const breadcrumb = this.getBreadcrumb();
        const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
        const canUseGenerativeAi = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use;
        const showCreateWithAiButton = canCreateResources && canUseGenerativeAi && !readOnly && !app.integrator;

        const canManageSharedSlides =
            !app.integrator &&
            !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type === PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    {t("Items in your trash will be permanently deleted after 30 days")}
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    {t("Empty Trash")}
                </WarningButton>
            </FlexBox>
        ) : null;

        return (
            // @ts-ignore
            <UIPaneWithSideBar className={className ?? ""}>
                <OnMountedReporter onMounted={onReady} />
                {!window.isZoomApp &&
                    <UIPaneSideBar
                        // @ts-ignore
                        mobileSideBar
                        show={showSidebar}
                        onHide={onHideSidebar}
                        style={{ width: sideBarWidth }}
                        onResize={onResizeSidebar}
                    >
                        <PresentationLibrarySideBar
                            filter={filter}
                            onSetFilter={this.handleSetFilter}
                            readOnly={readOnly}
                            draggedItems={draggedItems}
                            canEditLibraryItems={canManageSharedSlides}
                            onHideSidebar={onHideSidebar}
                            onFolderDropStart={this.handleFolderDropStart}
                        />
                    </UIPaneSideBar>
                }
                <UIPaneContents>
                    {
                        showCloseIcon &&
                        <CloseButtonContainer isMobile>
                            <Icon onClick={onCloseDialog}>
                                close
                            </Icon>
                        </CloseButtonContainer>
                    }

                    {window.roomID && (
                        window.isZoomApp
                            ? <RemoteControlBanner blue>{t("Select presentation to share in Zoom meeting.")}</RemoteControlBanner>
                            : <RemoteControlBanner>{t("Select a presentation to Remote Play")}</RemoteControlBanner>
                    )}
                    {!window.roomID && window.isZoomApp && (
                        <RemoteControlBanner blue>{t("Select a presentation to preview in player, please use the browser for full access.")}</RemoteControlBanner>
                    )}

                    {/* Scrollable content container */}
                    {/* @ts-ignore */}
                    <HiddenScrollBox id="presentation_list" ref={this.scrollRef}
                        fillWidth
                        style={{
                            height: 0,
                            flexGrow: 2,
                            position: "relative",
                            overflowY: "auto"
                        }}
                    >
                        {/* Content that scrolls away */}
                        <PresentationLibraryMobileIntro
                            breadcrumb={breadcrumb}
                            onCloseInfoBanner={() => this.handleInfoBannerClose()}
                            onCreateWithAi={() => browserHistory.push("/create-presentation/generate-with-ai")}
                            showCreateWithAiButton={showCreateWithAiButton}
                            showInfoBanner={!infoBannerClosed && showCreateWithAiButton}
                        />

                        {/* Sticky search bar */}
                        <FlexBox fillWidth horizontalAlign="start" style={{
                            position: "sticky",
                            top: 0,
                            padding: "24px 20px",
                            // To make sure the search bar will always be on top of the presentation items
                            // even when the image is still loading
                            zIndex: 1001,
                            backgroundColor: "white",
                        }}>
                            {
                                filter.type != PresentationFilters.TRASH &&
                                <PresentationListHeader
                                    onSearch={this.handleSearch}
                                    searchPlaceholder={t("Search presentations")}
                                    sortOptions={this.sortOptions}
                                    sort={sort[0]}
                                    searchQuery={search.query}
                                    onSetSort={this.handleSort}
                                    viewType={viewType}
                                    onSetViewType={this.handleSetViewType}
                                />
                            }
                            {TrashHeader}
                        </FlexBox>

                        {/* Presentation items */}
                        <StyledRenderItemsContainer>
                            {this.renderItems()}
                            <Gap30 />
                        </StyledRenderItemsContainer>
                    </HiddenScrollBox>
                </UIPaneContents>
                <ShortcutPanel />
            </UIPaneWithSideBar>
        );
    }
}

const InnerPresentationLibraryPane =
    AppController.withState(
        WorkspaceController.withState(
            PresentationLibraryPaneController.withState(
                withFirebaseUser(class InnerPresentationLibraryPane extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
                    render() {
                        if (isCompactLibrary()) {
                            return <InnerPresentationLibraryPaneMobile {...this.props} />;
                        }
                        return <InnerPresentationLibraryPaneDesktop {...this.props} />;
                    }
                }
                )
            )
        )
    );

export function PresentationLibraryPane(props: PresentationLibraryPaneProps & PresentationLibraryPaneControllerInitializerProps) {
    if (props.view === LibraryView.TEMPLATES && props.pane) {
        return (
            <LibraryTemplatesPane
                {...props}
                pane={props.pane as string}
            />
        );
    }

    return (
        <PresentationLibraryPaneControllerInitializer pane={props.pane} folderId={props.folderId} subFolderId={props.subFolderId}>
            <InnerPresentationLibraryPane {...props} />
        </PresentationLibraryPaneControllerInitializer>
    );
}
